<template>
  <div>
    <div class="relative">
      <div class="bg-white mb-6 px-20 py-12 rounded-lg">
        <div class="flex justify-between items-center mb-8">
          <div class="text-xl font-semibold text-gray-92 leading-8">
            <div>担当者情報</div>
          </div>
          <button
            class="block ml-auto bg-white text-coral-main font-semibold border border-coral-main rounded hover:button focus:outline-none"
            :class="{
              'text-gray-38': state.editForm,
              'border-gray-38': state.editForm,
              'cursor-default': state.editForm,
            }"
            style="width: 91px; height: 32px"
            v-if="!state.editForm"
            @click="toggleEdit()"
          >
            編集
          </button>
          <button
            v-else
            class="block ml-auto bg-white text-gray-62 font-semibold rounded hover:button focus:outline-none border border-gray-38"
            style="width: 91px; height: 32px"
            @click="toggleEdit()"
          >
            キャンセル
          </button>
        </div>

        <Form
          v-slot="{ handleSubmit, errors }"
          :validation-schema="state.validationSchema"
          as="div"
        >
          <form
            @submit="handleSubmit($event, onSubmit())"
            novalidate="novalidate"
          >
            <div v-for="(data, key) in state.accountData" :key="key">
              <div v-if="!state.editForm">
                <div v-if="state.hiddenItems[data.id] !== false" class="mb-8">
                  <div class="text-gray mb-2">{{ data.name }}</div>
                  <div
                    class="pb-2 font-light leading-6 border-b border-gray-12"
                  >
                    <span>
                      {{ state.inputString(data) }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else>
                <CompAccountFormParts
                  :form="data"
                  :errors="errors"
                  :hiddenItems="state.hiddenItems"
                  v-model:editData="state.editData"
                  @resetHiddenItemInput="resetHiddenItemInput"
                ></CompAccountFormParts>
              </div>
            </div>

            <button
              type="submit"
              class="block mt-12 font-semibold text-white rounded hover:button focus:outline-none"
              :class="{
                'bg-secondary-main': !Object.keys(errors).length,
                'bg-slate-300': Object.keys(errors).length,
              }"
              style="width: 200px; height: 40px"
              v-if="state.editForm"
              :disabled="Object.keys(errors).length"
            >
              変更を保存
            </button>
          </form>
        </Form>
      </div>
      <CompAccountCurrentEmail
        v-if="!state.isShowEmailEditForm"
        :currentEmail="state.currentEmail"
        @toggleEmailEditFormShow="toggleEmailEditFormShow"
      />
      <CompAccountEmail
        v-if="state.isShowEmailEditForm"
        :currentEmail="state.currentEmail"
        @toggleEmailEditFormShow="toggleEmailEditFormShow"
      />
    </div>
  </div>
</template>

<script>
import { reactive, computed, onMounted, inject } from 'vue';
import { Form } from 'vee-validate';
import { amountFormatter } from '@/utils/filters.js';
import { defaultData } from '@/views/account/js/defaultData.js';
import axios from '@/utils/axiosAuth.js';
import rules from '@/utils/validateRules.js';
import CompAccountFormParts from '@/views/account/vue/CompAccountFormParts.vue';
import CompAccountCurrentEmail from './CompAccountCurrentEmail.vue';
import CompAccountEmail from './CompAccountEmail.vue';

export default {
  components: {
    CompAccountFormParts,
    CompAccountCurrentEmail,
    CompAccountEmail,
    Form,
  },
  setup() {
    const globalMethods = inject('globalMethods');

    const state = reactive({
      seller: {},
      companyName: '',
      editData: {},
      inputData: {},
      validationSchema: {},
      editForm: false,
      accountData: defaultData,
      inputString: computed(() => (data) => {
        let str = '';
        if (data.input_type === 'text' || data.input_type === 'textarea') {
          str = data.unit ? data.values[0] + data.unit : data.values[0];
        } else if (data.input_type === 'checkbox') {
          const selected = data.options.filter(
            (option) =>
              data.values.findIndex((id) => {
                return id == option.id;
              }) >= 0
          );
          str = data.unit
            ? selected.map((e) => e.name).join(`${data.unit}` + '、')
            : selected.map((e) => e.name).join('、');
          str = str + (data.unit || '');
        } else if (
          data.input_type === 'select' ||
          data.input_type === 'radio'
        ) {
          const selected = data.options.find(
            (option) => option.id == data.values[0]
          );
          if (selected) {
            str = selected.name;
          }
          str = data.unit ? str + data.unit : str;
        }
        str =
          data.display_format_type === 'number' ? amountFormatter(str) : str;
        return str;
      }),
      hiddenItems: computed(() => {
        let hiddenItems = {};
        // 親項目を持つ項目を抽出
        for (const data of Object.values(state.accountData)) {
          for (const option of Object.values(data.options)) {
            checkInput: for (const displayItem of option.display_items.values()) {
              // 親項目が選択されていたらフラグ変更
              for (const inputItem of Object.values(state.editData)) {
                const index = inputItem.indexOf(option.id);
                if (index >= 0) {
                  hiddenItems[displayItem.item_id] = true;
                  break checkInput;
                }
              }
              hiddenItems[displayItem.item_id] = hiddenItems[
                displayItem.item_id
              ]
                ? true
                : false;
            }
          }
        }
        return hiddenItems;
      }),
      hasError: computed(() => (errors) => {
        for (const [key, value] of Object.entries(state.hiddenItems)) {
          if (!value) delete errors[`itemId${key}`];
        }
        return Object.keys(errors).length ? true : false;
      }),
      currentEmail: '',
      isShowEmailEditForm: false,
    });

    function toggleEdit() {
      state.editForm = !state.editForm;
      if (state.editForm) {
        resetEditData();
      }
    }

    function updateData() {
      // 入力値を更新
      const json = JSON.stringify(state.editData);
      const obj = JSON.parse(json);
      Object.assign(state.inputData, obj);
      // 表示値を更新
      for (const [index, val] of state.accountData.entries()) {
        for (const [key, val2] of Object.entries(state.inputData)) {
          if (val.id == key) {
            if (val.input_type === 'checkbox') {
              state.accountData[index].values = val2;
            } else {
              state.accountData[index].values[0] = val2[0];
            }
          }
        }
      }
    }

    function makePostData() {
      let postData = {
        name: state.editData.user_name[0],
        company_name: state.companyName,
        tel: state.editData.tel[0],
        items: [],
      };

      let obj = {};
      Object.assign(obj, state.editData);
      let items = Object.entries(obj).map(([key, value]) => ({
        id: key,
        values: value,
      }));

      postData.items = items.filter(
        (e) => e.id !== 'user_name' && e.id !== 'tel'
      );
      return postData;
    }

    const resetHiddenItemInput = () => {
      for (const [key, val] of Object.entries(state.hiddenItems)) {
        if (!val) {
          state.inputData[key] = [];
          state.editData[key] = [];
        }
      }
    };

    const toggleEmailEditFormShow = () => {
      state.isShowEmailEditForm = !state.isShowEmailEditForm;
    };

    const onSubmit = async () => {
      const postData = makePostData();
      await axios
        .post('/sellers/' + state.seller.id + '/update', postData)
        .then(() => {
          updateData();
          resetEditData();
          toggleEdit();
          globalMethods.isToast('変更が完了しました。', { timeout: 3000 });
        })
        .catch((e) => {
          globalMethods.updateModal(
            true,
            'リクエストが完了しませんでした。',
            e.systemMessage || '',
            e.callback
          );
          globalMethods.sendLog(e);
        });
    };

    onMounted(async () => {
      await axios
        .get('/sellers')
        .then((response) => {
          Object.assign(state.seller, response.data.data[0]);
        })
        .catch((e) => {
          globalMethods.updateModal(
            true,
            'リクエストが完了しませんでした。',
            e.systemMessage || '',
            e.callback
          );
          globalMethods.sendLog(e);
        });
      await axios
        .get(`/sellers/${state.seller.id}`)
        .then((response) => {
          state.companyName = response.data.data.company_name;
          state.currentEmail = response.data.data.email;
          makeDisplayData(response.data.data);
          makeInputData();
          makeValidationSchema();
        })
        .catch((e) => {
          globalMethods.updateModal(
            true,
            'URLが正しくないか、通信エラーが発生しました。',
            e.systemMessage || '',
            e.callback
          );
          globalMethods.sendLog(e);
        });
    });

    function makeDisplayData(data) {
      for (const [key, value] of Object.entries(data)) {
        if (key === 'items') {
          state.accountData = state.accountData.concat(value);
        } else {
          for (const [index, item] of state.accountData.entries()) {
            if (item.id == key) {
              state.accountData[index]['values'][0] = value;
            }
          }
        }
      }
    }

    function makeValidationSchema() {
      for (const val of state.accountData) {
        let dataTypeRule = '';
        switch (val.data_type) {
          case 'integer':
            dataTypeRule = '|integer';
            break;
          case 'decimal':
            dataTypeRule = '|double';
            break;
          case 'date':
            dataTypeRule = '|date';
            break;
        }
        state.validationSchema[`itemId${val.id}`] = async (value) => {
          return rules.validate(
            value,
            val.input_validation_rule + dataTypeRule
          );
        };
      }
    }

    function makeInputData() {
      state.accountData.forEach(
        (item) => (state.inputData[item.id] = item.values || [])
      );
      resetEditData();
    }

    function resetEditData() {
      const json = JSON.stringify(state.inputData);
      const obj = JSON.parse(json);
      state.editData = obj;
    }

    return {
      state,
      onSubmit,
      toggleEdit,
      resetHiddenItemInput,
      amountFormatter,
      toggleEmailEditFormShow,
    };
  },
};
</script>

<style lang="scss" scoped></style>
